import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MarketingNav } from "@/components/landing/MarketingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { HelpCircle, ChevronDown, Mail, MessageSquare } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { SchemaMarkup, generateBreadcrumbSchema, generateFAQSchema } from "@/components/SchemaMarkup";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const faqItems = [
  {
    q: { en: "How do I connect my CRM?", pt: "Como conecto meu CRM?" },
    a: {
      en: "Go to Settings → Integrations, select your CRM provider, and follow the authorization steps. The connection takes less than 5 minutes.",
      pt: "Vá em Configurações → Integrações, selecione seu provedor de CRM e siga os passos de autorização. A conexão leva menos de 5 minutos.",
    },
  },
  {
    q: { en: "How long does the initial data sync take?", pt: "Quanto tempo leva a sincronização inicial?" },
    a: {
      en: "The initial sync typically takes 5-30 minutes depending on the volume of data in your CRM. You'll see insights immediately after the first batch completes.",
      pt: "A sincronização inicial leva tipicamente de 5 a 30 minutos dependendo do volume de dados no seu CRM. Você verá insights imediatamente após o primeiro lote completar.",
    },
  },
  {
    q: { en: "Can I invite my team?", pt: "Posso convidar meu time?" },
    a: {
      en: "Yes! Go to Settings → Users and invite team members with different permission levels: Admin, Manager, Member, or Viewer.",
      pt: "Sim! Vá em Configurações → Usuários e convide membros com diferentes níveis de permissão: Admin, Gerente, Membro ou Visualizador.",
    },
  },
  {
    q: { en: "How is my forecast calculated?", pt: "Como meu forecast é calculado?" },
    a: {
      en: "Our forecast uses mathematical models based on your historical conversion rates, pipeline velocity, and seasonal patterns. It combines stage-weighted pipeline analysis with trend extrapolation for maximum accuracy.",
      pt: "Nosso forecast usa modelos matemáticos baseados em suas taxas históricas de conversão, velocidade do pipeline e padrões sazonais. Combina análise ponderada por estágio com extrapolação de tendência para máxima precisão.",
    },
  },
  {
    q: { en: "Can I export my data?", pt: "Posso exportar meus dados?" },
    a: {
      en: "Yes. You can export dashboards as PNG, reports as PDF, and raw data as CSV or Excel from the Data Export page.",
      pt: "Sim. Você pode exportar dashboards como PNG, relatórios como PDF e dados brutos como CSV ou Excel na página de Exportação de Dados.",
    },
  },
  {
    q: { en: "What happens if my CRM sync fails?", pt: "O que acontece se a sincronização do CRM falhar?" },
    a: {
      en: "You'll see a notification in the platform. All analytics continue working with the last synced dataset. Our system automatically retries failed syncs.",
      pt: "Você verá uma notificação na plataforma. Todas as análises continuam funcionando com o último dataset sincronizado. Nosso sistema automaticamente retenta syncs que falharam.",
    },
  },
  {
    q: { en: "How do I change my plan?", pt: "Como mudo meu plano?" },
    a: {
      en: "Go to Settings → Billing. You can upgrade or downgrade at any time. Changes take effect at the next billing cycle.",
      pt: "Vá em Configurações → Faturamento. Você pode fazer upgrade ou downgrade a qualquer momento. Mudanças entram em vigor no próximo ciclo de cobrança.",
    },
  },
  {
    q: { en: "Is my data safe?", pt: "Meus dados estão seguros?" },
    a: {
      en: "Absolutely. We use enterprise-grade encryption (TLS 1.3 + AES-256), SOC 2 compliant infrastructure, role-based access control, and complete data isolation per organization.",
      pt: "Com certeza. Usamos criptografia de nível empresarial (TLS 1.3 + AES-256), infraestrutura compatível com SOC 2, controle de acesso baseado em funções e isolamento completo de dados por organização.",
    },
  },
];

export default function HelpCenterPage() {
  const { i18n } = useTranslation();
  const isPt = i18n.language?.startsWith("pt");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={isPt ? "Central de Ajuda — FAQ e Suporte | RevOps AI" : "Help Center — FAQ & Support | RevOps AI"}
        description={isPt ? "Encontre respostas para dúvidas comuns sobre o RevOps AI. FAQ, suporte e guias de uso." : "Find answers to common questions about RevOps AI. FAQ, support and usage guides."}
        path="/help"
      />
      <SchemaMarkup schema={[
        generateBreadcrumbSchema([
          { name: 'Home', url: 'https://revops.digitais.ai/' },
          { name: isPt ? 'Central de Ajuda' : 'Help Center', url: 'https://revops.digitais.ai/help' },
        ]),
        generateFAQSchema(faqItems.map(f => ({ question: isPt ? f.q.pt : f.q.en, answer: isPt ? f.a.pt : f.a.en }))),
      ]} />
      <MarketingNav />
      <main>
        <section className="py-20 md:py-28 bg-[#F4F5FF]">
          <div className="container text-center max-w-3xl mx-auto">
            <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: isPt ? 'Central de Ajuda' : 'Help Center' }]} />
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-[#4F6CFF]/10 rounded-full text-sm font-medium text-[#4F6CFF]">
              <HelpCircle className="h-4 w-4" />
              {isPt ? "Central de Ajuda" : "Help Center"}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1A1F3C] mb-6">
              {isPt ? "Como podemos ajudar?" : "How can we help?"}
            </h1>
            <p className="text-lg text-[#1A1F3C]/60 mb-4">
              {isPt
                ? "Encontre respostas para as dúvidas mais comuns ou fale com nosso suporte."
                : "Find answers to common questions or talk to our support team."}
            </p>
            <p className="text-sm text-[#1A1F3C]/40 max-w-2xl mx-auto">
              {isPt
                ? "Saiba como conectar seu CRM, configurar integrações, convidar sua equipe, entender o forecast, exportar dados e muito mais. Se precisar de ajuda adicional, nossa equipe de suporte está disponível por e-mail."
                : "Learn how to connect your CRM, configure integrations, invite your team, understand forecasting, export data and more. If you need additional help, our support team is available via email."}
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="container max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-[#1A1F3C] mb-8">
              {isPt ? "Perguntas Frequentes" : "Frequently Asked Questions"}
            </h2>
            <div className="space-y-3">
              {faqItems.map((item, i) => (
                <div key={i} className="border border-[#1A1F3C]/10 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left font-medium text-[#1A1F3C] hover:bg-[#F4F5FF]/50 transition-colors"
                  >
                    {isPt ? item.q.pt : item.q.en}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform flex-shrink-0 ml-2 ${openIndex === i ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openIndex === i && (
                    <div className="px-5 pb-4 text-sm text-[#1A1F3C]/60 leading-relaxed">
                      {isPt ? item.a.pt : item.a.en}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-[#F4F5FF]/50">
          <div className="container max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-[#1A1F3C] mb-8 text-center">
              {isPt ? "Fale com o Suporte" : "Contact Support"}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <a
                href="mailto:suporte@digitais.ai"
                className="flex items-start gap-4 p-6 rounded-xl border border-[#1A1F3C]/10 hover:border-[#4F6CFF]/30 transition-colors"
              >
                <div className="p-2 rounded-lg bg-[#4F6CFF]/10">
                  <Mail className="h-5 w-5 text-[#4F6CFF]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#1A1F3C] mb-1">{isPt ? "Email" : "Email"}</h3>
                  <p className="text-sm text-[#1A1F3C]/60">suporte@digitais.ai</p>
                  <p className="text-xs text-[#1A1F3C]/40 mt-1">
                    {isPt ? "Resposta em até 24h úteis" : "Response within 24 business hours"}
                  </p>
                </div>
              </a>
              <a
                href="mailto:suporte@digitais.ai"
                className="flex items-start gap-4 p-6 rounded-xl border border-[#1A1F3C]/10 hover:border-[#4F6CFF]/30 transition-colors"
              >
                <div className="p-2 rounded-lg bg-[#00C896]/10">
                  <MessageSquare className="h-5 w-5 text-[#00C896]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#1A1F3C] mb-1">{isPt ? "Chat ao Vivo" : "Live Chat"}</h3>
                  <p className="text-sm text-[#1A1F3C]/60">{isPt ? "Dentro da plataforma" : "Inside the platform"}</p>
                  <p className="text-xs text-[#1A1F3C]/40 mt-1">
                    {isPt ? "Disponível em horário comercial" : "Available during business hours"}
                  </p>
                </div>
              </a>
            </div>
          </div>
        </section>
      </main>
      <LandingFooter />
    </div>
  );
}
